import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { styled } from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { HeadlineM } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_CONTENDER_CONTENDER } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { navLabels } from '@sorare/core/src/lib/glossary';

import { Contender } from 'components/lineup/Contender';

import type { MyLineups_so5LeaderboardContender } from './__generated__/index.graphql';

const ContenderList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: var(--double-unit);
`;

type Props = {
  so5LeaderboardContenders: MyLineups_so5LeaderboardContender[];
};

export const MyLineups = ({ so5LeaderboardContenders }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const isTabletAndAbove = useIsTabletAndAbove();

  if (so5LeaderboardContenders.length === 0) {
    return null;
  }

  const contenders = so5LeaderboardContenders.map(contender => (
    <Contender
      key={contender.slug}
      so5LeaderboardContender={contender}
      lineupStyle="forced_inline"
      to={generateSportContextPath(
        ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_CONTENDER_CONTENDER,
        {
          params: {
            eventType: contender.so5Leaderboard.so5Fixture.type.toLowerCase(),
            fixture: contender.so5Leaderboard.so5Fixture.slug,
            contender: contender.slug,
          },
        }
      )}
    />
  ));

  return (
    <Vertical gap={3}>
      <HeadlineM bold>
        <FormattedMessage {...navLabels.myLineups} />
      </HeadlineM>
      {isTabletAndAbove ? (
        <ContenderList>{contenders}</ContenderList>
      ) : (
        <Scrollable itemToDisplay={1} overhang={3}>
          {contenders}
        </Scrollable>
      )}
    </Vertical>
  );
};

MyLineups.fragments = {
  so5LeaderboardContender: gql`
    fragment MyLineups_so5LeaderboardContender on So5LeaderboardContender {
      slug
      so5Leaderboard {
        slug
        so5Fixture {
          slug
          type
        }
      }
      ...Contender_so5LeaderboardContender
    }
    ${Contender.fragments.so5LeaderboardContender}
  ` as TypedDocumentNode<MyLineups_so5LeaderboardContender>,
};
